import { AxiosInstance } from "axios"
import { INTEGRATION_OPERATION } from "types/dataIntegration.types"
import { resolveAsyncJson } from "api/asyncJob"
import type {
  IntegrateDataParams,
  IntegrateDataResponse,
} from "types/dataIntegration.types"

/**
 * Data-integration API function - Uses the authenticated axios instance.
 * Authentication headers are handled automatically by the axios instance.
 */

/**
 * Merges the data files (DQD) of two projects into a NEW data-only project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param params - The integration parameters. `operation` "add" appends cases
 *   (option `keep`); "connect" joins horizontally on the '//'-separated
 *   `idPath` (option `join`). `name` is the new project's name. `deriveQuery`
 *   and `firstWave` optionally run a saved query against each side and stamp a
 *   WAVE variable on each before merging.
 * @returns The parsed conductor response. The endpoint is async-capable, so this
 *   resolves either from the immediate 200 or after polling the job to completion.
 * @throws Will throw an error if the request fails.
 */
export async function integrateData(
  axiosInstance: AxiosInstance,
  params: IntegrateDataParams
): Promise<IntegrateDataResponse> {
  let url =
    `/data-integrate?base_id=${params.baseId}` +
    `&base_owner=${encodeURIComponent(params.baseOwner)}` +
    `&add_id=${params.addId}` +
    `&add_owner=${encodeURIComponent(params.addOwner)}` +
    `&operation=${params.operation}`
  if (params.operation === INTEGRATION_OPERATION.ADD && params.keep) {
    url += `&keep=${params.keep}`
  }
  if (params.operation === INTEGRATION_OPERATION.CONNECT) {
    if (params.join) url += `&join=${params.join}`
    if (params.idPath) url += `&id_path=${encodeURIComponent(params.idPath)}`
  }
  if (params.deriveQuery) {
    url += `&derive_query=${encodeURIComponent(params.deriveQuery)}`
    if (params.querySourceId) {
      url += `&query_source_id=${params.querySourceId}`
    }
    if (params.querySourceOwner) {
      url += `&query_source_owner=${encodeURIComponent(params.querySourceOwner)}`
    }
  }
  // Wave stamping is meaningless for connect (same respondents, one WAVE column),
  // and the server rejects the combination outright.
  if (
    params.operation === INTEGRATION_OPERATION.ADD &&
    params.firstWave !== undefined &&
    params.firstWave !== null
  ) {
    url += `&first_wave=${params.firstWave}`
  }
  // The server applies Run on load by default; only the opt-out is sent.
  if (params.applyOnLoad === false) {
    url += "&onload=off"
  }

  // Deriving both waves and merging them can outrun the proxy's 120s limit, so the
  // endpoint is async-capable. responseType/transformResponse keep axios from parsing
  // the 202 body — a bare job id — as JSON; resolveAsyncJson parses the final result.
  const response = await axiosInstance.post<string>(
    url,
    { name: params.name },
    {
      headers: { "ASync-Request": 10 },
      responseType: "text",
      transformResponse: (data) => data,
    }
  )
  return resolveAsyncJson<IntegrateDataResponse>(
    axiosInstance,
    response,
    "Data integration"
  )
}
